import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { useFetchAuditoriaLogs } from '@/shared/actions/erp/comisiones/comisiones.actions';
import type { ParametroChangeLog } from '@/lib/commissions/types';
import { formatDateWithTime } from '@/shared/utils/format-date';

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return 'Sin valor';
  }
  if (typeof value === 'boolean') {
    return value ? 'Si' : 'No';
  }
  if (typeof value === 'number') {
    return value.toString();
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (_error) {
    return String(value);
  }
};

const resolveValue = (log: ParametroChangeLog, container: Record<string, any> | null): string => {
  if (!container || typeof container !== 'object') {
    return formatValue(container);
  }

  if (log.campo && container[log.campo] !== undefined) {
    return formatValue(container[log.campo]);
  }

  const keys = Object.keys(container);
  if (keys.length === 1) {
    return formatValue(container[keys[0]]);
  }

  return formatValue(container);
};

export default function AuditoriaTimeline() {
  const { data: logs, isLoading, error } = useFetchAuditoriaLogs({}, true);
  const [searchValue, setSearchValue] = useState<string>('');

  const filteredLogs = useMemo(() => {
    if (!logs) return [];
    const term = searchValue.trim().toLowerCase();

    if (!term) return logs;

    return logs.filter((log) => {
      const searchable = [
        log.tabla,
        log.tabla_display,
        log.campo,
        log.campo_display,
        log.usuario_nombre,
        JSON.stringify(log.valor_anterior),
        JSON.stringify(log.valor_nuevo),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(term);
    });
  }, [logs, searchValue]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Auditoria de Cambios
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Historial completo de cambios en los parámetros de comisiones. Busca por tabla, campo, usuario o valor.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Buscar por tabla, campo, usuario o valor..."
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          fullWidth
        />
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">
          No se pudo cargar la auditoria. Intenta nuevamente mas tarde.
        </Alert>
      )}

      {!isLoading && !error && filteredLogs.length === 0 && (
        <Alert severity="info">No se encontraron registros de auditoria para los filtros actuales.</Alert>
      )}

      {!isLoading && !error && filteredLogs.length > 0 && (
        <Box
          sx={{
            position: 'relative',
            maxWidth: 900,
            mx: 'auto',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: { xs: 6, sm: 14 },
              width: 2,
              bgcolor: 'divider',
            },
          }}
        >
          <Stack spacing={2} sx={{ position: 'relative', pl: { xs: 0, sm: 2 } }}>
            {filteredLogs.map((log, index) => {
              const key = log.id ?? `${log.tabla}-${log.obj_id ?? 'sin-id'}-${log.fecha}-${index}`;
              const usuario = log.usuario_nombre || `Usuario #${log.usuario ?? 'N/D'}`;
              const fechaFormateada = log.fecha
                ? formatDateWithTime(log.fecha)
                : 'Fecha no disponible';

              const valorAnterior = resolveValue(log, log.valor_anterior);
              const valorNuevo = resolveValue(log, log.valor_nuevo);
              const isDeleted = log.campo === '__deleted__';
              const tablaDisplay = log.tabla_display || log.tabla;
              const campoDisplay = log.campo_display || log.campo;

              return (
                <Box key={key} sx={{ position: 'relative', pl: { xs: 4, sm: 5 } }}>
                  <Avatar
                    sx={{
                      position: 'absolute',
                      left: { xs: 6, sm: 14 },
                      top: 12,
                      transform: 'translate(-50%, 0)',
                      width: 32,
                      height: 32,
                      bgcolor: isDeleted ? 'error.main' : 'primary.main',
                      boxShadow: (theme) => `0 0 0 3px ${theme.palette.background.default}`,
                    }}
                  >
                    {isDeleted ? <DeleteIcon sx={{ fontSize: 18 }} /> : <EditIcon sx={{ fontSize: 18 }} />}
                  </Avatar>

                  <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{
                      px: 2,
                      py: 1.5,
                      '&:hover': {
                        boxShadow: 1,
                        borderColor: 'primary.main',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {/* Header compacto */}
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        <Chip
                          icon={<PersonIcon />}
                          label={usuario}
                          size="small"
                          variant="outlined"
                          sx={{ height: 24, fontSize: '0.75rem' }}
                        />
                        <Chip
                          label={tablaDisplay}
                          size="small"
                          color="primary"
                          sx={{ height: 24, fontSize: '0.75rem' }}
                        />
                        {!isDeleted && (
                          <Chip
                            label={campoDisplay}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ height: 24, fontSize: '0.75rem' }}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {fechaFormateada}
                      </Typography>
                    </Stack>

                    {/* Valores - Layout más compacto */}
                    {!isDeleted && (
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 1.5 }}
                        alignItems="center"
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Anterior
                          </Typography>
                          <Box
                            sx={{
                              px: 1,
                              py: 0.75,
                              borderRadius: 0.5,
                              bgcolor: 'grey.50',
                              border: '1px solid',
                              borderColor: 'grey.200',
                              fontSize: '0.813rem',
                              fontFamily: 'monospace',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'text.secondary',
                            }}
                            title={valorAnterior}
                          >
                            {valorAnterior}
                          </Box>
                        </Box>

                        <Box sx={{ color: 'text.secondary', fontSize: '1.2rem', flexShrink: 0 }}>→</Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="success.dark" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Nuevo
                          </Typography>
                          <Box
                            sx={{
                              px: 1,
                              py: 0.75,
                              borderRadius: 0.5,
                              bgcolor: 'success.50',
                              border: '1px solid',
                              borderColor: 'success.200',
                              fontSize: '0.813rem',
                              fontFamily: 'monospace',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'success.dark',
                              fontWeight: 500,
                            }}
                            title={valorNuevo}
                          >
                            {valorNuevo}
                          </Box>
                        </Box>
                      </Stack>
                    )}

                    {/* Mensaje para eliminados */}
                    {isDeleted && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>
                          Registro eliminado (ID: {log.obj_id})
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
